// ============================================
// Creator Editor - Images Module (ES6)
// 다중 이미지 관리, 프리뷰
// ============================================

import { showToast } from '../../utils.js';
import { getCurrentEditorData } from '../state.js';
import { renderIntroPreview } from '../intro-blocks.js';
import { updatePublishButtonState, updateImageValidationState } from './events.js';
import { showConfirm } from '../../shared/confirm-modal.js';

// ============================================
// 다중 이미지 관리
// ============================================

export function renderEditorImages() {
  const container = document.getElementById('editorImagesContainer');
  const addBtn = document.getElementById('editorImageAddBtn');
  const countEl = document.getElementById('editorImageCount');
  if (!container) return;

  const MAX_IMAGES = 5;
  const currentEditorData = getCurrentEditorData();

  if (countEl) {
    countEl.textContent = `${currentEditorData.images.length}/${MAX_IMAGES}`;
  }

  container.innerHTML = currentEditorData.images.map((img, idx) => `
    <div class="editor-image-item${idx === 0 ? ' main' : ''}" data-index="${idx}" data-action="openPreview">
      <img src="${img.url}" alt="점술가 이미지 ${idx + 1}">
      <button class="image-delete-btn" data-action="removeImage" data-index="${idx}">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
      ${idx === 0 ? '<div class="main-badge">대표</div>' : ''}
    </div>
  `).join('');

  // 이벤트 위임 설정
  container.onclick = (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;
    const idx = parseInt(actionEl.dataset.index ?? actionEl.closest('[data-index]')?.dataset.index, 10);

    if (action === 'removeImage') {
      e.stopPropagation();
      removeEditorImage(idx);
    } else if (action === 'openPreview') {
      openEditorImagePreview(idx);
    }
  };

  if (addBtn) {
    addBtn.classList.toggle('hidden', currentEditorData.images.length >= MAX_IMAGES);
  }

  renderIntroPreview();
}

export function addEditorImage(url, file = null) {
  const MAX_IMAGES = 5;
  const currentEditorData = getCurrentEditorData();
  if (currentEditorData.images.length >= MAX_IMAGES) {
    showToast(`이미지는 최대 ${MAX_IMAGES}개까지 추가할 수 있습니다`);
    return false;
  }

  currentEditorData.images.push({ url, file });
  renderEditorImages();
  updateImageValidationState();
  updatePublishButtonState();
  return true;
}

export function removeEditorImage(index) {
  const currentEditorData = getCurrentEditorData();
  if (index < 0 || index >= currentEditorData.images.length) return;

  currentEditorData.images.splice(index, 1);
  renderEditorImages();
  updateImageValidationState();
  updatePublishButtonState();
}

export function selectMainImage(index) {
  const currentEditorData = getCurrentEditorData();
  if (index < 0 || index >= currentEditorData.images.length) return;
  if (index === 0) return;

  const selected = currentEditorData.images.splice(index, 1)[0];
  currentEditorData.images.unshift(selected);
  renderEditorImages();
  updatePublishButtonState();
  showToast('대표 이미지가 변경되었습니다');
}

export function getMainImageUrl() {
  const currentEditorData = getCurrentEditorData();
  return currentEditorData.images.length > 0 ? currentEditorData.images[0].url : null;
}

// ============================================
// 이미지 프리뷰 모달
// ============================================

export function openEditorImagePreview(index) {
  const currentEditorData = getCurrentEditorData();
  if (index < 0 || index >= currentEditorData.images.length) return;

  const img = currentEditorData.images[index];
  const isMain = index === 0;

  const modalHtml = `
    <div id="editorImagePreviewModal" class="modal-overlay" data-action="closePreview">
      <div class="image-preview-content">
        <div class="image-preview-container">
          <img src="${img.url}" alt="이미지 미리보기">
        </div>
        <div class="image-preview-actions">
          ${!isMain ? `
            <button class="preview-action-btn set-main" data-action="setMain" data-index="${index}">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              <span>대표로 설정</span>
            </button>
          ` : `
            <button class="preview-action-btn set-main disabled" disabled>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              <span>대표 이미지</span>
            </button>
          `}
          <button class="preview-action-btn download" data-action="download" data-index="${index}" title="다운로드">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
          <button class="preview-action-btn delete" data-action="removeFromPreview" data-index="${index}" title="삭제">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
        </div>
        <button class="image-preview-close" data-action="closePreview">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>
  `;

  const existingModal = document.getElementById('editorImagePreviewModal');
  if (existingModal) existingModal.remove();

  document.body.insertAdjacentHTML('beforeend', modalHtml);

  // 이벤트 위임 설정
  const modal = document.getElementById('editorImagePreviewModal');
  modal.addEventListener('click', (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;
    const idx = parseInt(actionEl.dataset.index, 10);

    // content 내부 클릭 시 닫히지 않도록
    if (e.target.closest('.image-preview-content') && action !== 'closePreview' && action !== 'setMain' && action !== 'download' && action !== 'removeFromPreview') {
      return;
    }

    if (action === 'closePreview') {
      closeEditorImagePreview();
    } else if (action === 'setMain') {
      setMainFromPreview(idx);
    } else if (action === 'download') {
      downloadPreviewImage(idx);
    } else if (action === 'removeFromPreview') {
      removeFromPreview(idx);
    }
  });

  document.body.style.overflow = 'hidden';
}

export function closeEditorImagePreview(event) {
  if (event) event.stopPropagation();
  const modal = document.getElementById('editorImagePreviewModal');
  if (modal) {
    modal.remove();
    document.body.style.overflow = '';
  }
}

export function setMainFromPreview(index) {
  closeEditorImagePreview();
  selectMainImage(index);
}

export function removeFromPreview(index) {
  closeEditorImagePreview();
  removeEditorImage(index);
}

export async function downloadPreviewImage(index) {
  const currentEditorData = getCurrentEditorData();
  if (index < 0 || index >= currentEditorData.images.length) return;

  const img = currentEditorData.images[index];
  try {
    const response = await fetch(img.url);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `character_image_${index + 1}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showToast('이미지가 다운로드되었습니다');
  } catch (error) {
    console.error('이미지 다운로드 실패:', error);
    window.open(img.url, '_blank');
  }
}

// ============================================
// 에디터 메뉴
// ============================================

export function showEditorMenu(event) {
  event.stopPropagation();

  const existingMenu = document.querySelector('.editor-context-menu');
  if (existingMenu) existingMenu.remove();

  const menu = document.createElement('div');
  menu.className = 'creator-item-menu editor-context-menu';
  const currentEditorData = getCurrentEditorData();
  menu.innerHTML = `
    <button data-action="resetEditor">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
      </svg>
      초기화
    </button>
    ${currentEditorData.id ? `
      <button class="danger" data-action="deleteFromEditor">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>
        삭제
      </button>
    ` : ''}
  `;

  // 이벤트 위임 설정
  menu.addEventListener('click', (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;
    if (action === 'resetEditor') {
      resetEditor();
    } else if (action === 'deleteFromEditor') {
      deleteFromEditor();
    }
    menu.remove();
  });

  document.body.appendChild(menu);

  const rect = event.target.getBoundingClientRect();
  menu.style.top = `${rect.bottom + 5}px`;
  menu.style.right = `${window.innerWidth - rect.right}px`;

  setTimeout(() => {
    document.addEventListener('click', function closeMenu() {
      menu.remove();
      document.removeEventListener('click', closeMenu);
    });
  }, 0);
}

export async function resetEditor() {
  const confirmed = await showConfirm({
    message: '모든 내용을 초기화하시겠습니까?',
    confirmText: '초기화',
    isDanger: true
  });
  if (confirmed) {
    const currentEditorData = getCurrentEditorData();
    currentEditorData.id = null;
    currentEditorData.imageUrl = null;
    currentEditorData.imageFile = null;
    // Import dynamically to avoid circular dependency
    import('./form.js').then(({ resetEditorForm }) => {
      resetEditorForm();
    });
    showToast('초기화되었습니다');
  }
}

export function deleteFromEditor() {
  const currentEditorData = getCurrentEditorData();
  if (currentEditorData.id && window.CreatorModule) {
    window.CreatorModule.confirmDelete(currentEditorData.id);
    import('./modal.js').then(({ closeEditor }) => {
      closeEditor();
    });
  }
}
